import React from 'react';
import { formatCurrency } from '../utils/formatCurrency';
interface BillHeaderProps {
  company: {
    name: string;
    address: string[];
    phone: string;
    email: string;
  };
  bill: {
    number: string;
    status: 'Submitted' | 'Approved' | 'Paid';
    billDate: string;
    dueDate: string;
    totalDueAmount: number;
    matchedAmount: number;
    outstandingAmount: number;
    createdBy: string;
    lastUpdated: string;
    printedBy: string;
    printingDate: string;
  };
}
export function BillHeader({
  company,
  bill
}: BillHeaderProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Approved':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Submitted':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };
  return <>
      {/* Top Row: Logo Left, Address Right */}
      <div className="flex justify-between items-start mb-4" data-id="element-46">
        {/* Left: Logo */}
        <div data-id="element-47">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md" data-id="element-48">
            <span className="text-white font-bold text-lg" data-id="element-49">MT</span>
          </div>
        </div>

        {/* Right: Company Address - Compact */}
        <div className="text-right" data-id="element-50">
          <h1 className="text-sm text-neutral-900 mb-1" data-id="element-51">{company.name}</h1>
          <div className="text-[10px] text-neutral-500 space-y-0.5 leading-relaxed" data-id="element-52">
            {company.address.map((line, i) => <p key={i} data-id="element-53">{line}</p>)}
            <p className="mt-1" data-id="element-54">
              Tel: {company.phone} | Email: {company.email}
            </p>
          </div>
        </div>
      </div>

      {/* SUPPLIER BILL Title - Centered Above Box */}
      <h2 className="text-lg font-bold text-neutral-900 mb-2 tracking-tight text-center" data-id="element-55">
        SUPPLIER BILL
      </h2>

      {/* Bill Details Box */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-4" data-id="element-56">
        {/* Two Column Grid */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5" data-id="element-57">
          {/* Left Column */}
          <div className="space-y-1.5" data-id="element-58">
            <div className="flex justify-between items-center" data-id="element-59">
              <span className="text-xs font-medium text-neutral-600" data-id="element-60">
                Bill No.:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-61">
                {bill.number}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-62">
              <span className="text-xs font-medium text-neutral-600" data-id="element-63">
                Status:
              </span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${getStatusColor(bill.status)}`} data-id="element-64">
                {bill.status}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-65">
              <span className="text-xs font-medium text-neutral-600" data-id="element-66">
                Bill Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-67">{bill.billDate}</span>
            </div>
            <div className="flex justify-between" data-id="element-68">
              <span className="text-xs font-medium text-neutral-600" data-id="element-69">
                Due Date:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-70">
                {bill.dueDate}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-71">
              <span className="text-xs font-medium text-neutral-600" data-id="element-72">
                Total Due Amount:
              </span>
              <span className="text-xs font-semibold text-neutral-900 tabular-nums" data-id="element-73">
                {formatCurrency(bill.totalDueAmount)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-74">
              <span className="text-xs font-medium text-neutral-600" data-id="element-75">
                Matched Amount:
              </span>
              <span className="text-xs text-green-600 tabular-nums" data-id="element-76">
                {formatCurrency(bill.matchedAmount)}
              </span>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-1.5" data-id="element-77">
            <div className="flex justify-between" data-id="element-78">
              <span className="text-xs font-medium text-neutral-600" data-id="element-79">
                Outstanding Amount:
              </span>
              <span className="text-xs font-semibold text-red-600 tabular-nums" data-id="element-80">
                {formatCurrency(bill.outstandingAmount)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-81">
              <span className="text-xs font-medium text-neutral-600" data-id="element-82">
                Created By:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-83">{bill.createdBy}</span>
            </div>
            <div className="flex justify-between" data-id="element-84">
              <span className="text-xs font-medium text-neutral-600" data-id="element-85">
                Last Updated:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-86">
                {bill.lastUpdated}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-87">
              <span className="text-xs font-medium text-neutral-600" data-id="element-88">
                Printed By:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-89">{bill.printedBy}</span>
            </div>
            <div className="flex justify-between" data-id="element-90">
              <span className="text-xs font-medium text-neutral-600" data-id="element-91">
                Printing Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-92">
                {bill.printingDate}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>;
}